import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { toast } from 'sonner';
import {
    ChevronLeft, ChevronRight, Check, X, Loader2,
    Target, BarChart3, Layout, Calendar, Layers, Save,
    CheckCircle2, AlertTriangle, Users, BookOpen, Clock,
    ArrowLeftRight, Eye, Upload, RefreshCw, Zap, Play,
    Settings2, ChevronDown as ChevronDownIcon, Filter
} from 'lucide-react';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { ScrollArea } from '../ui/scroll-area';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Switch } from '../ui/switch';
import { Progress } from '../ui/progress';
import DayScheduleViewer from './DayScheduleViewer';
import api from '../../api';
import useCalendarStore from '../../store/useCalendarStore';

const LoadingFallback = () => (
    <div className="text-center py-8 text-sm text-zinc-400">
        <Loader2 className="w-5 h-5 animate-spin mx-auto mb-2 text-blue-500" />
        Processing...
    </div>
);

// ═══════════════════════════════════════════════════════════
// STEP INDICATOR
// ═══════════════════════════════════════════════════════════
function StepIndicator({ steps, currentStep, completedSteps }) {
    return (
        <div className="flex items-center gap-1 px-4 py-3 border-b border-zinc-200 dark:border-zinc-700 bg-zinc-50/50 dark:bg-zinc-800/30">
            {steps.map((step, i) => {
                const stepNum = i + 1;
                const isActive = stepNum === currentStep;
                const isDone = completedSteps.includes(stepNum);
                return (
                    <React.Fragment key={step.id}>
                        {i > 0 && (
                            <div className={`flex-1 h-px max-w-8 ${isDone || isActive ? 'bg-blue-400' : 'bg-zinc-300 dark:bg-zinc-600'}`} />
                        )}
                        <div className={`flex items-center gap-1.5 px-2 py-1 rounded-md transition-all text-xs
                            ${isActive ? 'bg-blue-500/10 text-blue-600 dark:text-blue-400 font-semibold' :
                                isDone ? 'text-emerald-600 dark:text-emerald-400' : 'text-zinc-400'}`}>
                            <div className={`w-5 h-5 rounded-full flex items-center justify-center text-[0.6rem] font-bold
                                ${isActive ? 'bg-blue-500 text-white' :
                                    isDone ? 'bg-emerald-500 text-white' :
                                        'bg-zinc-200 dark:bg-zinc-600 text-zinc-500 dark:text-zinc-400'}`}>
                                {isDone ? <Check className="w-3 h-3" /> : stepNum}
                            </div>
                            <span className="hidden sm:inline">{step.label}</span>
                        </div>
                    </React.Fragment>
                );
            })}
        </div>
    );
}

// ═══════════════════════════════════════════════════════════
// STEP 1: SCOPE SELECTOR
// ═══════════════════════════════════════════════════════════
function ScopeStep({ onSubmit, loading }) {
    const [batches, setBatches] = useState([]);
    const [faculty, setFaculty] = useState([]);
    const [selectedBatches, setSelectedBatches] = useState([]);
    const [selectedFaculty, setSelectedFaculty] = useState([]);
    const [startDate, setStartDate] = useState('');
    const [endDate, setEndDate] = useState('');

    useEffect(() => {
        (async () => {
            try {
                const [bRes, fRes] = await Promise.all([
                    api.get('/api/batches'),
                    api.get('/api/faculty'),
                ]);
                setBatches(bRes.data || []);
                setFaculty(fRes.data || []);
            } catch { /* silent */ }
        })();
    }, []);

    const toggleBatch = (id) => setSelectedBatches(s =>
        s.includes(id) ? s.filter(x => x !== id) : [...s, id]
    );
    const toggleFaculty = (id) => setSelectedFaculty(s =>
        s.includes(id) ? s.filter(x => x !== id) : [...s, id]
    );

    return (
        <div className="space-y-5">
            <div className="data-card p-4 space-y-3">
                <h3 className="text-sm font-semibold text-zinc-700 dark:text-zinc-200 flex items-center gap-1.5">
                    <Calendar className="w-4 h-4 text-blue-500" /> Date Range
                </h3>
                <div className="grid grid-cols-2 gap-3">
                    <div>
                        <Label className="text-xs">Start Date</Label>
                        <Input type="date" value={startDate} onChange={e => setStartDate(e.target.value)} className="mt-1" />
                    </div>
                    <div>
                        <Label className="text-xs">End Date</Label>
                        <Input type="date" value={endDate} onChange={e => setEndDate(e.target.value)} className="mt-1" />
                    </div>
                </div>
            </div>

            <div className="data-card p-4 space-y-3">
                <h3 className="text-sm font-semibold text-zinc-700 dark:text-zinc-200 flex items-center gap-1.5">
                    <BookOpen className="w-4 h-4 text-emerald-500" /> Batches
                    <Badge variant="secondary" className="text-[0.6rem]">{selectedBatches.length} selected</Badge>
                </h3>
                <div className="flex flex-wrap gap-2">
                    {batches.map(b => (
                        <button key={b.id} onClick={() => toggleBatch(b.id)}
                            className={`px-3 py-1.5 rounded-md text-xs border transition-all
                                ${selectedBatches.includes(b.id)
                                    ? 'bg-blue-500 text-white border-blue-500'
                                    : 'bg-zinc-50 dark:bg-zinc-800 text-zinc-600 dark:text-zinc-300 border-zinc-200 dark:border-zinc-700 hover:border-blue-400'}`}>
                            {b.name}
                        </button>
                    ))}
                    {batches.length === 0 && <span className="text-xs text-zinc-400">No batches found</span>}
                </div>
            </div>

            <div className="data-card p-4 space-y-3">
                <h3 className="text-sm font-semibold text-zinc-700 dark:text-zinc-200 flex items-center gap-1.5">
                    <Users className="w-4 h-4 text-violet-500" /> Faculty
                    <Badge variant="secondary" className="text-[0.6rem]">{selectedFaculty.length} selected</Badge>
                </h3>
                <div className="flex flex-wrap gap-2">
                    {faculty.map(f => (
                        <button key={f.id} onClick={() => toggleFaculty(f.id)}
                            className={`px-3 py-1.5 rounded-md text-xs border transition-all
                                ${selectedFaculty.includes(f.id)
                                    ? 'bg-violet-500 text-white border-violet-500'
                                    : 'bg-zinc-50 dark:bg-zinc-800 text-zinc-600 dark:text-zinc-300 border-zinc-200 dark:border-zinc-700 hover:border-violet-400'}`}>
                            {f.name}
                        </button>
                    ))}
                    {faculty.length === 0 && <span className="text-xs text-zinc-400">No faculty found</span>}
                </div>
                <p className="text-[0.65rem] text-zinc-400">Leave empty to include all faculty</p>
            </div>

            <Button onClick={() => {
                if (!startDate || !endDate) {
                    toast.error("Please select a date range.");
                    return;
                }
                if (selectedBatches.length === 0 && selectedFaculty.length === 0) {
                    toast.error("Please select at least one batch or faculty.");
                    return;
                }
                onSubmit({ batch_ids: selectedBatches, faculty_ids: selectedFaculty, start_date: startDate, end_date: endDate });
            }}
                disabled={loading}
                className="w-full">
                {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Target className="w-4 h-4 mr-2" />}
                Confirm Scope
            </Button>
        </div>
    );
}

// ═══════════════════════════════════════════════════════════
// STEP 2: FEASIBILITY DASHBOARD
// ═══════════════════════════════════════════════════════════
function FeasibilityStep({ data, onContinue, loading }) {
    if (!data) return <div className="text-center py-8"><Loader2 className="w-5 h-5 animate-spin mx-auto text-blue-500" /></div>;

    return (
        <div className="space-y-4">
            <div className={`data-card p-4 border-l-4 ${data.feasible ? 'border-l-emerald-500' : 'border-l-red-500'}`}>
                <div className="flex items-center gap-2 mb-2">
                    {data.feasible
                        ? <CheckCircle2 className="w-5 h-5 text-emerald-500" />
                        : <AlertTriangle className="w-5 h-5 text-red-500" />}
                    <span className="font-semibold text-sm text-zinc-800 dark:text-zinc-100">
                        {data.feasible ? 'Schedule is feasible' : 'Capacity insufficient'}
                    </span>
                </div>
                <p className="text-xs text-zinc-400">
                    Capacity ratio: <strong className="text-zinc-600 dark:text-zinc-300">{data.capacity_ratio}x</strong>
                    {data.capacity_ratio >= 2 && ' — Plenty of room for preferences'}
                    {data.capacity_ratio >= 1 && data.capacity_ratio < 2 && ' — Tight but feasible'}
                    {data.capacity_ratio < 1 && ' — Not enough capacity'}
                </p>
            </div>

            {data.bottlenecks?.length > 0 && (
                <div className="data-card p-4">
                    <h4 className="text-xs font-semibold text-amber-600 dark:text-amber-400 uppercase mb-2 flex items-center gap-1.5">
                        <AlertTriangle className="w-3.5 h-3.5" /> Bottlenecks
                    </h4>
                    <div className="space-y-1.5">
                        {data.bottlenecks.map((b, i) => (
                            <div key={i} className="text-xs text-zinc-500 dark:text-zinc-400">
                                <strong className="text-zinc-700 dark:text-zinc-300">{b.name}</strong>: {b.reason}
                            </div>
                        ))}
                    </div>
                </div>
            )}

            <div className="grid grid-cols-3 gap-3">
                <div className="data-card p-3 text-center">
                    <div className="text-lg font-bold text-zinc-800 dark:text-zinc-100">{data.total_slots || 0}</div>
                    <div className="text-[0.65rem] text-zinc-400">Total Slots</div>
                </div>
                <div className="data-card p-3 text-center">
                    <div className="text-lg font-bold text-zinc-800 dark:text-zinc-100">{data.required_slots || 0}</div>
                    <div className="text-[0.65rem] text-zinc-400">Required</div>
                </div>
                <div className="data-card p-3 text-center">
                    <div className="text-lg font-bold text-emerald-500">{data.surplus_slots || 0}</div>
                    <div className="text-[0.65rem] text-zinc-400">Surplus</div>
                </div>
            </div>

            <Button onClick={onContinue} disabled={loading} className="w-full">
                {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <ChevronRight className="w-4 h-4 mr-2" />}
                Continue to Template Discovery
            </Button>
        </div>
    );
}

// ═══════════════════════════════════════════════════════════
// STEP 3: TEMPLATE DISCOVERY
// ═══════════════════════════════════════════════════════════
function TemplateDiscoveryStep({ data, onContinue, loading }) {
    if (!data) return <div className="text-center py-8"><Loader2 className="w-5 h-5 animate-spin mx-auto text-blue-500" /></div>;

    const discovery = data.discovery || {};
    const clusters = discovery.clusters || [];
    const created = data.created_templates || [];

    return (
        <div className="space-y-4">
            <div className="data-card p-4">
                <div className="flex items-center justify-between mb-2">
                    <h4 className="text-sm font-semibold text-zinc-700 dark:text-zinc-200 flex items-center gap-1.5">
                        <Layout className="w-4 h-4 text-blue-500" /> Discovered Patterns
                    </h4>
                    <Badge variant="secondary">{discovery.unique_patterns || 0} unique</Badge>
                </div>
                <p className="text-xs text-zinc-400 mb-3">
                    Analyzed {discovery.total_days || 0} days and found {clusters.length} pattern clusters based on availability fingerprints.
                </p>

                {clusters.length > 0 ? (
                    <div className="space-y-2">
                        {clusters.map((c, i) => (
                            <div key={i} className="flex items-center justify-between px-3 py-2 rounded-lg bg-zinc-50 dark:bg-zinc-800/50 border border-zinc-200 dark:border-zinc-700">
                                <div>
                                    <div className="text-xs font-medium text-zinc-700 dark:text-zinc-200">{c.suggested_name || `Pattern ${i + 1}`}</div>
                                    <div className="text-[0.6rem] text-zinc-400">Fingerprint: {(c.fingerprint || '').substring(0, 8)}... · {c.count} days</div>
                                </div>
                                <Badge variant="outline" className="text-[0.6rem]">{c.count}×</Badge>
                            </div>
                        ))}
                    </div>
                ) : (
                    <div className="text-xs text-zinc-400 text-center py-4 border border-dashed border-zinc-200 dark:border-zinc-700 rounded-lg">
                        No distinct patterns found — all days have similar constraints
                    </div>
                )}
            </div>

            {created.length > 0 && (
                <div className="data-card p-4">
                    <h4 className="text-xs font-semibold text-emerald-600 dark:text-emerald-400 uppercase mb-2">
                        Auto-Created Templates
                    </h4>
                    <div className="space-y-1">
                        {created.map((t, i) => (
                            <div key={i} className="text-xs text-zinc-500">
                                ✓ {t.name || 'Template'} — matches {t.match_count || 0} days
                            </div>
                        ))}
                    </div>
                </div>
            )}

            <Button onClick={onContinue} disabled={loading} className="w-full">
                {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <ChevronRight className="w-4 h-4 mr-2" />}
                Continue to Weekly Pattern
            </Button>
        </div>
    );
}

// ═══════════════════════════════════════════════════════════
// STEP 4: WEEKLY PATTERN
// ═══════════════════════════════════════════════════════════
function WeeklyPatternStep({ templates, onSubmit, loading }) {
    const days = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'];
    const dayLabels = { mon: 'Monday', tue: 'Tuesday', wed: 'Wednesday', thu: 'Thursday', fri: 'Friday', sat: 'Saturday', sun: 'Sunday' };
    const [pattern, setPattern] = useState({});

    return (
        <div className="space-y-4">
            <div className="data-card p-4">
                <h4 className="text-sm font-semibold text-zinc-700 dark:text-zinc-200 mb-3 flex items-center gap-1.5">
                    <Calendar className="w-4 h-4 text-blue-500" /> Assign Templates to Weekdays
                </h4>
                <p className="text-xs text-zinc-400 mb-4">
                    Map each weekday to a template. Days without a template will not be scheduled.
                </p>
                <div className="space-y-2">
                    {days.map(d => (
                        <div key={d} className="flex items-center gap-3">
                            <span className="w-24 text-xs font-medium text-zinc-600 dark:text-zinc-300">{dayLabels[d]}</span>
                            <select
                                value={pattern[d] || ''}
                                onChange={e => setPattern(p => ({ ...p, [d]: e.target.value || undefined }))}
                                className="flex-1 h-8 rounded-md border border-zinc-200 dark:border-zinc-700 bg-white dark:bg-zinc-800 
                                    text-xs text-zinc-700 dark:text-zinc-200 px-2 focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                            >
                                <option value="">— No template —</option>
                                {templates.map(t => (
                                    <option key={t.id} value={t.id}>{t.name} ({(t.events || []).length} events)</option>
                                ))}
                            </select>
                        </div>
                    ))}
                </div>
            </div>

            <Button onClick={() => onSubmit({ pattern })} disabled={loading || Object.keys(pattern).filter(k => pattern[k]).length === 0}
                className="w-full">
                {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Layers className="w-4 h-4 mr-2" />}
                Apply Weekly Pattern
            </Button>
        </div>
    );
}

// ═══════════════════════════════════════════════════════════
// STEP 5: POPULATE TIMELINE
// ═══════════════════════════════════════════════════════════
function PopulateStep({ data, onContinue, loading }) {
    if (!data) return <div className="text-center py-8"><Loader2 className="w-5 h-5 animate-spin mx-auto text-blue-500" /></div>;

    const timeline = data.timeline || [];
    const matchColors = {
        exact: 'bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-300 border-emerald-300 dark:border-emerald-700',
        preference_only: 'bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300 border-amber-300 dark:border-amber-700',
        partial: 'bg-orange-100 dark:bg-orange-900/30 text-orange-700 dark:text-orange-300 border-orange-300 dark:border-orange-700',
        full_resolve: 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300 border-red-300 dark:border-red-700',
        unknown: 'bg-zinc-100 dark:bg-zinc-800 text-zinc-600 dark:text-zinc-400 border-zinc-300 dark:border-zinc-600',
    };

    return (
        <div className="space-y-4">
            <div className="grid grid-cols-4 gap-3">
                <div className="data-card p-3 text-center">
                    <div className="text-lg font-bold text-zinc-800 dark:text-zinc-100">{data.total_days}</div>
                    <div className="text-[0.65rem] text-zinc-400">Days</div>
                </div>
                <div className="data-card p-3 text-center">
                    <div className="text-lg font-bold text-emerald-500">{data.exact_match_days}</div>
                    <div className="text-[0.65rem] text-zinc-400">Exact Match</div>
                </div>
                <div className="data-card p-3 text-center">
                    <div className="text-lg font-bold text-blue-500">{data.reusable_days}</div>
                    <div className="text-[0.65rem] text-zinc-400">Reusable</div>
                </div>
                <div className="data-card p-3 text-center">
                    <div className="text-lg font-bold text-amber-500">{data.needs_resolve_days}</div>
                    <div className="text-[0.65rem] text-zinc-400">Needs Resolve</div>
                </div>
            </div>

            <div className="data-card p-4">
                <h4 className="text-sm font-semibold text-zinc-700 dark:text-zinc-200 mb-3">Timeline Preview</h4>
                <ScrollArea className="max-h-64">
                    <div className="space-y-1">
                        {timeline.map((day, i) => (
                            <div key={i} className={`flex items-center justify-between px-3 py-1.5 rounded-md text-xs border ${matchColors[day.match_level] || matchColors.unknown}`}>
                                <div className="flex items-center gap-2">
                                    <span className="font-medium w-20">{day.date}</span>
                                    <span className="text-[0.6rem] uppercase">{day.weekday}</span>
                                </div>
                                <div className="flex items-center gap-2">
                                    <span className="truncate max-w-[120px]">{day.template_name}</span>
                                    <Badge variant="outline" className="text-[0.55rem]">{day.match_level}</Badge>
                                </div>
                            </div>
                        ))}
                    </div>
                </ScrollArea>
            </div>

            <Button onClick={onContinue} disabled={loading} className="w-full">
                {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <ChevronRight className="w-4 h-4 mr-2" />}
                Continue to Save
            </Button>
        </div>
    );
}

// ═══════════════════════════════════════════════════════════
// STEP 6: SAVE BLUEPRINT
// ═══════════════════════════════════════════════════════════
function SaveStep({ onSubmit, loading, previousData }) {
    const [name, setName] = useState('');

    return (
        <div className="space-y-4">
            <div className="data-card p-5 space-y-4">
                <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-blue-500/10 flex items-center justify-center">
                        <Save className="w-5 h-5 text-blue-500" />
                    </div>
                    <div>
                        <h3 className="text-sm font-semibold text-zinc-800 dark:text-zinc-100">Save Blueprint</h3>
                        <p className="text-xs text-zinc-400">Name your tentative schedule to save it</p>
                    </div>
                </div>

                <div>
                    <Label className="text-xs">Blueprint Name</Label>
                    <Input
                        value={name}
                        onChange={e => setName(e.target.value)}
                        placeholder="e.g. Spring 2026 Blueprint"
                        className="mt-1"
                    />
                </div>

                {previousData?.total_days && (
                    <div className="text-xs text-zinc-400 space-y-0.5">
                        <div>• {previousData.total_days} days scheduled</div>
                        <div>• {previousData.exact_match_days} exact template matches</div>
                        <div>• {previousData.reusable_days} reusable from templates</div>
                    </div>
                )}
            </div>

            <Button onClick={() => onSubmit({ name: name || 'Untitled Blueprint' })}
                disabled={loading} className="w-full bg-blue-600 hover:bg-blue-700">
                {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
                Save Blueprint
            </Button>
        </div>
    );
}

// ═══════════════════════════════════════════════════════════
// COMPLETION SCREEN
// ═══════════════════════════════════════════════════════════
function CompletionScreen({ result, onClose, generatedSchedules, scope, pipelineType }) {
    const [templateName, setTemplateName] = useState('');
    const [saving, setSaving] = useState(false);
    const [saved, setSaved] = useState(false);
    const [isStarred, setIsStarred] = useState(false);

    const isFixedDay = pipelineType === 'fixed_day';
    const totalEvents = generatedSchedules
        ? generatedSchedules.reduce((s, d) => s + (d.events?.length || 0), 0)
        : (result?.event_count || 0);

    const handleSaveTemplate = async () => {
        if (!generatedSchedules?.length) return;
        setSaving(true);
        try {
            const events = generatedSchedules.flatMap(d => (d.events || []).map(ev => ({
                assignment_id: ev.assignment_id,
                faculty_id: ev.faculty_id,
                batch_id: ev.batch_id,
                start_slot: ev.start_slot,
                duration_slots: ev.duration_slots,
                subject_name: ev.subject_name,
                faculty_name: ev.faculty_name,
                batch_name: ev.batch_name,
            })));
            const name = templateName.trim() || `Template — ${generatedSchedules[0]?.date || 'Untitled'}`;
            await api.post('/api/templates', {
                name,
                scope: scope || {},
                events,
                is_starred: isStarred,
            });
            setSaved(true);
            toast.success(`Template "${name}" saved with ${events.length} events`);
        } catch (err) {
            toast.error(err?.response?.data?.detail || 'Failed to save template');
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="flex flex-col items-center justify-center py-10 space-y-5">
            <div className="w-16 h-16 rounded-full bg-emerald-500/10 flex items-center justify-center animate-fade-in-up">
                <CheckCircle2 className="w-8 h-8 text-emerald-500" />
            </div>
            <h3 className="text-lg font-bold text-zinc-800 dark:text-zinc-100">
                {isFixedDay ? 'Schedule Published!' : 'Blueprint Created!'}
            </h3>
            <div className="text-sm text-zinc-500 text-center space-y-1">
                {isFixedDay
                    ? <p><strong className="text-zinc-700 dark:text-zinc-300">{totalEvents}</strong> events published to main calendar</p>
                    : <p><strong className="text-zinc-700 dark:text-zinc-300">{result?.name || 'Blueprint'}</strong> saved as draft</p>
                }
            </div>

            {/* Save as Template — only for fixed_day with generated data */}
            {isFixedDay && generatedSchedules?.length > 0 && (
                <div className="w-full max-w-sm data-card p-4 space-y-3">
                    <h4 className="text-xs font-semibold text-zinc-600 dark:text-zinc-300 flex items-center gap-1.5">
                        <Layout className="w-3.5 h-3.5 text-blue-500" />
                        Save as Reusable Template
                    </h4>
                    {saved ? (
                        <div className="flex items-center gap-2 text-sm text-emerald-600">
                            <Check className="w-4 h-4" /> Template saved successfully
                        </div>
                    ) : (
                        <>
                            <Input
                                placeholder={`Template — ${generatedSchedules[0]?.date || 'Day'}`}
                                value={templateName}
                                onChange={e => setTemplateName(e.target.value)}
                                className="text-xs h-8"
                            />
                            <div className="flex items-center gap-2 mt-1">
                                <input 
                                    type="checkbox" 
                                    id="star_template" 
                                    checked={isStarred}
                                    onChange={e => setIsStarred(e.target.checked)}
                                    className="w-3 h-3 rounded border-zinc-300 text-blue-600 focus:ring-blue-500"
                                />
                                <label htmlFor="star_template" className="text-[0.6rem] text-zinc-500 cursor-pointer flex items-center gap-1">
                                    <Star className={cn("w-2.5 h-2.5", isStarred && "text-amber-500 fill-current")} />
                                    Star this template
                                </label>
                            </div>
                            <p className="text-[0.6rem] text-zinc-400">
                                Save this {totalEvents}-event schedule as a template to reuse on matching days.
                            </p>
                            <Button onClick={handleSaveTemplate} disabled={saving} size="sm"
                                className="w-full bg-blue-600 hover:bg-blue-700 h-8 text-xs">
                                {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1.5" /> : <Save className="w-3.5 h-3.5 mr-1.5" />}
                                Save Template
                            </Button>
                        </>
                    )}
                </div>
            )}

            <Button onClick={onClose} className="mt-2" variant="outline">
                <ChevronLeft className="w-4 h-4 mr-2" /> Back to Scheduling
            </Button>
        </div>
    );
}

// ═══════════════════════════════════════════════════════════
// FIXED DAY — STEP 1: SELECT DATE & ADVANCED SETTINGS
// ═══════════════════════════════════════════════════════════
function SliderRow({ label, value, min, max, step, onChange, format, hint }) {
    return (
        <div className="space-y-1.5">
            <div className="flex items-center justify-between">
                <span className="text-[0.7rem] font-medium text-zinc-600 dark:text-zinc-300">{label}</span>
                <span className="text-[0.7rem] font-bold text-blue-500 tabular-nums">{format ? format(value) : value}</span>
            </div>
            <input
                type="range" min={min} max={max} step={step} value={value}
                onChange={e => onChange(parseFloat(e.target.value))}
                className="w-full h-1.5 rounded-full accent-blue-500 cursor-pointer"
            />
            {hint && <p className="text-[0.6rem] text-zinc-400">{hint}</p>}
        </div>
    );
}

function FixedDaySelectStep({ onSubmit, loading }) {
    const [targetDate, setTargetDate] = useState('');
    const [templateId, setTemplateId] = useState('');
    const [templates, setTemplates] = useState([]);
    const [assignments, setAssignments] = useState([]);
    const [selectedAssignments, setSelectedAssignments] = useState([]);
    const [showAdvanced, setShowAdvanced] = useState(false);
    const [assignmentSearch, setAssignmentSearch] = useState('');

    // Reward weights
    const [activationWeight, setActivationWeight] = useState(10);
    const [preferenceWeight, setPreferenceWeight] = useState(5);
    const [compactnessWeight, setCompactnessWeight] = useState(2);
    const [morningWeight, setMorningWeight] = useState(3);

    // Break settings
    const [lunchBreakStart, setLunchBreakStart] = useState('');
    const [lunchBreakEnd, setLunchBreakEnd] = useState('');
    const [maxConsecutiveSlots, setMaxConsecutiveSlots] = useState(0);

    useEffect(() => {
        (async () => {
            try {
                const [tRes, aRes] = await Promise.all([
                    api.get('/api/templates', { params: { session_id: 'none' } }),
                    api.get('/api/assignments'),
                ]);
                setTemplates(tRes.data || []);
                setAssignments(aRes.data || []);
            } catch { /* silent */ }
        })();
    }, []);

    const toggleAssignment = (id) => setSelectedAssignments(s =>
        s.includes(id) ? s.filter(x => x !== id) : [...s, id]
    );

    const filteredAssignments = assignments.filter(a => {
        if (!assignmentSearch) return true;
        const q = assignmentSearch.toLowerCase();
        return (a.subject_name || '').toLowerCase().includes(q) ||
            (a.faculty_name || '').toLowerCase().includes(q) ||
            (a.batch_name || '').toLowerCase().includes(q);
    });

    // Convert HH:MM to slot index (15-min slots, starting from 08:00 = slot 0)
    const timeToSlot = (time) => {
        if (!time) return null;
        const [h, m] = time.split(':').map(Number);
        return (h * 60 + m - 8 * 60) / 15;
    };

    const handleSubmit = () => {
        if (!targetDate) return;
        const scope_assignment_ids = selectedAssignments.length > 0 ? selectedAssignments : undefined;
        
        const settings_override = {};
        if (maxConsecutiveSlots > 0) settings_override.max_consecutive_slots = maxConsecutiveSlots;
        const lsSlot = timeToSlot(lunchBreakStart);
        const leSlot = timeToSlot(lunchBreakEnd);
        if (lsSlot !== null && leSlot !== null && leSlot > lsSlot) {
            settings_override.lunch_break_start_slot = lsSlot;
            settings_override.lunch_break_end_slot = leSlot;
        }
        
        onSubmit({
            target_date: targetDate,
            template_id: templateId || undefined,
            scope_assignment_ids,
            morning_preference: morningWeight,
            compactness: compactnessWeight,
            faculty_preference: preferenceWeight,
            fulfillment_priority: activationWeight,
            settings_override,
        });
    };

    return (
        <div className="space-y-4">
            {/* Date */}
            <div className="data-card p-4 space-y-3">
                <h3 className="text-sm font-semibold text-zinc-700 dark:text-zinc-200 flex items-center gap-1.5">
                    <Calendar className="w-4 h-4 text-emerald-500" /> Select Date to Schedule
                </h3>
                <Input type="date" value={targetDate} onChange={e => setTargetDate(e.target.value)} />
            </div>

            {/* Template */}
            <div className="data-card p-4 space-y-3">
                <h3 className="text-sm font-semibold text-zinc-700 dark:text-zinc-200 flex items-center gap-1.5">
                    <Layout className="w-4 h-4 text-blue-500" /> Template (optional)
                </h3>
                <select value={templateId} onChange={e => setTemplateId(e.target.value)}
                    className="w-full h-9 rounded-md border border-zinc-200 dark:border-zinc-700 bg-white dark:bg-zinc-800 text-xs px-2">
                    <option value="">Auto-select best template</option>
                    {templates.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
                </select>
                <p className="text-[0.65rem] text-zinc-400">If empty, the system auto-matches based on fingerprint</p>
            </div>

            {/* Advanced Settings Toggle */}
            <button
                onClick={() => setShowAdvanced(v => !v)}
                className="w-full flex items-center justify-between px-4 py-2.5 rounded-xl border border-zinc-200 dark:border-zinc-700 bg-zinc-50 dark:bg-zinc-800/60 hover:bg-zinc-100 dark:hover:bg-zinc-800 transition-all text-xs font-semibold text-zinc-600 dark:text-zinc-300"
            >
                <span className="flex items-center gap-2">
                    <Settings2 className="w-3.5 h-3.5 text-violet-500" />
                    Advanced Scheduler Settings
                    {(selectedAssignments.length > 0 || maxConsecutiveSlots > 0 || lunchBreakStart) && (
                        <span className="bg-violet-500 text-white text-[0.55rem] font-bold px-1.5 py-0.5 rounded-full">
                            {[selectedAssignments.length > 0 && `${selectedAssignments.length} assignments`, maxConsecutiveSlots > 0 && `max ${maxConsecutiveSlots} slots`, lunchBreakStart && 'lunch'].filter(Boolean).length} active
                        </span>
                    )}
                </span>
                <ChevronDownIcon className={`w-4 h-4 transition-transform ${showAdvanced ? 'rotate-180' : ''}`} />
            </button>

            {showAdvanced && (
                <div className="space-y-3 animate-fade-in-up">
                    {/* Selective Assignments */}
                    <div className="data-card p-4 space-y-3">
                        <div className="flex items-center justify-between">
                            <h3 className="text-sm font-semibold text-zinc-700 dark:text-zinc-200 flex items-center gap-1.5">
                                <Filter className="w-3.5 h-3.5 text-blue-500" /> Schedule Only These Assignments
                            </h3>
                            {selectedAssignments.length > 0 && (
                                <button onClick={() => setSelectedAssignments([])} className="text-[0.6rem] text-zinc-400 hover:text-red-500 transition-colors">
                                    Clear all
                                </button>
                            )}
                        </div>
                        <p className="text-[0.65rem] text-zinc-400">
                            Leave empty to schedule all assignments. Select specific ones to restrict the solver scope.
                        </p>
                        <div className="relative">
                            <BookOpen className="w-3 h-3 absolute left-2.5 top-1/2 -translate-y-1/2 text-zinc-400" />
                            <input
                                value={assignmentSearch}
                                onChange={e => setAssignmentSearch(e.target.value)}
                                placeholder="Search by subject, faculty, batch…"
                                className="w-full h-8 pl-7 pr-3 rounded-md border border-zinc-200 dark:border-zinc-700 bg-white dark:bg-zinc-800 text-xs focus:outline-none focus:ring-1 focus:ring-blue-500"
                            />
                        </div>
                        <div className="max-h-44 overflow-y-auto space-y-1 pr-0.5">
                            {filteredAssignments.length === 0 && (
                                <p className="text-[0.65rem] text-zinc-400 text-center py-3">No assignments found</p>
                            )}
                            {filteredAssignments.map(a => {
                                const sel = selectedAssignments.includes(a.id);
                                return (
                                    <label key={a.id} className={cn(
                                        "flex items-center gap-2.5 px-3 py-2 rounded-lg border cursor-pointer transition-all text-xs",
                                        sel
                                            ? 'border-blue-300 dark:border-blue-700 bg-blue-50 dark:bg-blue-900/20'
                                            : 'border-transparent hover:border-zinc-200 dark:hover:border-zinc-700 hover:bg-zinc-50 dark:hover:bg-zinc-800'
                                    )}>
                                        <div className={cn(
                                            'w-4 h-4 rounded border flex items-center justify-center flex-shrink-0 transition-all',
                                            sel ? 'bg-blue-600 border-blue-600' : 'border-zinc-300 dark:border-zinc-600'
                                        )}>
                                            {sel && <Check className="w-2.5 h-2.5 text-white" strokeWidth={3} />}
                                        </div>
                                        <input type="checkbox" className="hidden" checked={sel} onChange={() => toggleAssignment(a.id)} />
                                        <div className="flex-1 min-w-0">
                                            <div className="font-medium text-zinc-700 dark:text-zinc-200 truncate">{a.subject_name}</div>
                                            <div className="text-[0.6rem] text-zinc-400">{a.faculty_name} · {a.batch_name}</div>
                                        </div>
                                        {a.group_id && (
                                            <span className="text-[0.55rem] font-bold bg-violet-100 dark:bg-violet-900/30 text-violet-600 dark:text-violet-400 px-1.5 py-0.5 rounded-full">
                                                GROUP
                                            </span>
                                        )}
                                    </label>
                                );
                            })}
                        </div>
                        {selectedAssignments.length > 0 && (
                            <div className="flex items-center gap-1.5 flex-wrap pt-1">
                                {selectedAssignments.map(id => {
                                    const a = assignments.find(x => x.id === id);
                                    return a ? (
                                        <span key={id} className="inline-flex items-center gap-1 text-[0.6rem] bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 px-2 py-0.5 rounded-full">
                                            {a.subject_name}
                                            <button onClick={() => toggleAssignment(id)} className="hover:text-red-500 transition-colors">×</button>
                                        </span>
                                    ) : null;
                                })}
                            </div>
                        )}
                    </div>

                    <div className="data-card p-4 space-y-4">
                        <h3 className="text-sm font-semibold text-zinc-700 dark:text-zinc-200 flex items-center gap-1.5">
                            <BarChart3 className="w-3.5 h-3.5 text-emerald-500" /> Scheduling Quality Preferences
                        </h3>
                        <p className="text-[0.65rem] text-zinc-400 -mt-1">Tune how the optimizer balances competing goals for this day.</p>
                        <div className="space-y-3">
                            <SliderRow
                                label="Fulfillment Priority"
                                value={activationWeight} min={1} max={20} step={1}
                                onChange={setActivationWeight}
                                hint="Higher = stronger effort to fit every requested assignment."
                            />
                            <SliderRow
                                label="Faculty Preference"
                                value={preferenceWeight} min={0} max={15} step={1}
                                onChange={setPreferenceWeight}
                                hint="Higher = prioritizes faculty-marked preferred time slots."
                            />
                            <SliderRow
                                label="Compactness"
                                value={compactnessWeight} min={0} max={10} step={1}
                                onChange={setCompactnessWeight}
                                hint="Higher = minimizes large gaps between classes for students."
                            />
                            <SliderRow
                                label="Morning Bias"
                                value={morningWeight} min={0} max={10} step={1}
                                onChange={setMorningWeight}
                                hint="Higher = prefers scheduling classes earlier in the day."
                            />
                        </div>
                    </div>

                    {/* Break & Fatigue Constraints */}
                    <div className="data-card p-4 space-y-4">
                        <h3 className="text-sm font-semibold text-zinc-700 dark:text-zinc-200 flex items-center gap-1.5">
                            <Clock className="w-3.5 h-3.5 text-amber-500" /> Break & Fatigue Constraints
                        </h3>
                        <div className="space-y-3">
                            <div className="space-y-1.5">
                                <label className="text-[0.7rem] font-medium text-zinc-600 dark:text-zinc-300 flex items-center justify-between">
                                    Mandatory Lunch Break Window
                                    {lunchBreakStart && lunchBreakEnd && (
                                        <span className="text-[0.65rem] text-amber-600 dark:text-amber-400 font-bold">
                                            {lunchBreakStart} – {lunchBreakEnd}
                                        </span>
                                    )}
                                </label>
                                <div className="grid grid-cols-2 gap-2">
                                    <div>
                                        <span className="text-[0.6rem] text-zinc-400 block mb-1">Break Start</span>
                                        <input type="time" value={lunchBreakStart} onChange={e => setLunchBreakStart(e.target.value)}
                                            className="w-full h-8 rounded-md border border-zinc-200 dark:border-zinc-700 bg-white dark:bg-zinc-800 text-xs px-2 focus:ring-1 focus:ring-amber-500 focus:border-amber-500" />
                                    </div>
                                    <div>
                                        <span className="text-[0.6rem] text-zinc-400 block mb-1">Break End</span>
                                        <input type="time" value={lunchBreakEnd} onChange={e => setLunchBreakEnd(e.target.value)}
                                            className="w-full h-8 rounded-md border border-zinc-200 dark:border-zinc-700 bg-white dark:bg-zinc-800 text-xs px-2 focus:ring-1 focus:ring-amber-500 focus:border-amber-500" />
                                    </div>
                                </div>
                                <p className="text-[0.6rem] text-zinc-400">At least one 15-min free slot will be enforced within this window.</p>
                            </div>
                            <div className="space-y-1.5">
                                <div className="flex items-center justify-between">
                                    <span className="text-[0.7rem] font-medium text-zinc-600 dark:text-zinc-300">Max Consecutive Teaching Slots</span>
                                    <span className="text-[0.7rem] font-bold text-amber-500 tabular-nums">
                                        {maxConsecutiveSlots === 0 ? 'Off' : `${maxConsecutiveSlots} slots (${maxConsecutiveSlots * 15}m)`}
                                    </span>
                                </div>
                                <input type="range" min={0} max={16} step={1} value={maxConsecutiveSlots}
                                    onChange={e => setMaxConsecutiveSlots(parseInt(e.target.value))}
                                    className="w-full h-1.5 rounded-full accent-amber-500 cursor-pointer" />
                                <p className="text-[0.6rem] text-zinc-400">Prevents faculty from teaching back-to-back beyond this limit. 0 = disabled.</p>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            <Button onClick={handleSubmit}
                disabled={loading || !targetDate} className="w-full bg-emerald-600 hover:bg-emerald-700">
                {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Play className="w-4 h-4 mr-2" />}
                {selectedAssignments.length > 0
                    ? `Schedule ${selectedAssignments.length} selected assignment${selectedAssignments.length > 1 ? 's' : ''} for ${targetDate || '…'}`
                    : `Start Scheduling for ${targetDate || '…'}`
                }
            </Button>
        </div>
    );
}

// ═══════════════════════════════════════════════════════════
// FIXED DAY — SANDBOX PREVIEW + PUBLISH
// ═══════════════════════════════════════════════════════════
function SandboxPreviewStep({ sessionId, onPublish, onContinue, loading, generatedSchedules, faculty, batches }) {
    // Derive comparison from local generatedSchedules data directly
    const totalEvents = useMemo(() => {
        if (!generatedSchedules) return 0;
        return generatedSchedules.reduce((acc, s) => acc + (s.events?.length || 0), 0);
    }, [generatedSchedules]);

    const totalDropped = useMemo(() => {
        if (!generatedSchedules) return 0;
        return generatedSchedules.reduce((acc, s) => acc + (s.dropped?.length || 0), 0);
    }, [generatedSchedules]);

    const avgCoverage = useMemo(() => {
        if (!generatedSchedules?.length) return 0;
        const sum = generatedSchedules.reduce((acc, s) => acc + (s.scores?.coverage_pct || 0), 0);
        return Math.round(sum / generatedSchedules.length);
    }, [generatedSchedules]);

    return (
        <div className="space-y-4">
            {/* Calendar Day-View Preview */}
            {generatedSchedules?.length > 0 && (
                <DayScheduleViewer
                    schedules={generatedSchedules}
                    faculty={faculty || []}
                    batches={batches || []}
                    readOnly
                />
            )}

            <div className="data-card p-4">
                <h4 className="text-sm font-semibold text-zinc-700 dark:text-zinc-200 mb-3 flex items-center gap-1.5">
                    <Eye className="w-4 h-4 text-emerald-500" /> Sandbox vs Current Calendar
                </h4>
                <div className="grid grid-cols-2 gap-4">
                    <div>
                        <div className="text-[0.65rem] text-zinc-400 uppercase mb-1">Current</div>
                        <div className="text-lg font-bold text-zinc-700 dark:text-zinc-200">—</div>
                    </div>
                    <div>
                        <div className="text-[0.65rem] text-zinc-400 uppercase mb-1">Sandbox (Generated)</div>
                        <div className="text-lg font-bold text-emerald-500">{totalEvents} events</div>
                    </div>
                </div>
                <div className="flex gap-3 mt-3 text-xs">
                    <Badge className="bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300">
                        +{totalEvents} new
                    </Badge>
                    {totalDropped > 0 && (
                        <Badge className="bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300">
                            ⚠ {totalDropped} dropped
                        </Badge>
                    )}
                    <Badge className="bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300">
                        {avgCoverage}% coverage
                    </Badge>
                </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
                <Button variant="outline" onClick={() => onContinue?.()} disabled={loading}>
                    <X className="w-4 h-4 mr-2" /> Discard
                </Button>
                <Button onClick={onPublish} disabled={loading} className="bg-emerald-600 hover:bg-emerald-700">
                    {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Upload className="w-4 h-4 mr-2" />}
                    Publish to Calendar
                </Button>
            </div>
        </div>
    );
}

// ═══════════════════════════════════════════════════════════
// POST-UPDATE — REQUEST SELECTION
// ═══════════════════════════════════════════════════════════
function PostUpdateRequestStep({ onSubmit, loading }) {
    const [changeType, setChangeType] = useState('swap');
    const [eventId, setEventId] = useState('');
    const [reason, setReason] = useState('');

    const changeTypes = [
        { id: 'swap', label: 'Swap', desc: 'Exchange time slots between two events', icon: ArrowLeftRight, color: 'text-blue-500' },
        { id: 'reschedule', label: 'Reschedule', desc: 'Move event to a different time or day', icon: RefreshCw, color: 'text-violet-500' },
        { id: 'cancel', label: 'Cancel', desc: 'Remove event from schedule', icon: X, color: 'text-red-500' },
    ];

    return (
        <div className="space-y-4">
            <div className="data-card p-4 space-y-3">
                <h3 className="text-sm font-semibold text-zinc-700 dark:text-zinc-200 flex items-center gap-1.5">
                    <Zap className="w-4 h-4 text-violet-500" /> Change Type
                </h3>
                <div className="space-y-2">
                    {changeTypes.map(ct => (
                        <button key={ct.id} onClick={() => setChangeType(ct.id)}
                            className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg border transition-all text-left
                                ${changeType === ct.id
                                    ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20'
                                    : 'border-zinc-200 dark:border-zinc-700 hover:border-blue-400'}`}>
                            <ct.icon className={`w-4 h-4 ${ct.color}`} />
                            <div>
                                <div className="text-xs font-medium text-zinc-700 dark:text-zinc-200">{ct.label}</div>
                                <div className="text-[0.6rem] text-zinc-400">{ct.desc}</div>
                            </div>
                        </button>
                    ))}
                </div>
            </div>

            <div className="data-card p-4 space-y-3">
                <div>
                    <Label className="text-xs">Event ID or Title (optional)</Label>
                    <Input value={eventId} onChange={e => setEventId(e.target.value)}
                        placeholder="Search by event ID or title..." className="mt-1" />
                </div>
                <div>
                    <Label className="text-xs">Reason</Label>
                    <Input value={reason} onChange={e => setReason(e.target.value)}
                        placeholder="Faculty unavailable, room change..." className="mt-1" />
                </div>
            </div>

            <Button onClick={() => onSubmit({ change_type: changeType, event_id: eventId, reason })}
                disabled={loading} className="w-full bg-violet-600 hover:bg-violet-700">
                {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <ChevronRight className="w-4 h-4 mr-2" />}
                Find Alternatives
            </Button>
        </div>
    );
}

// ═══════════════════════════════════════════════════════════
// POST-UPDATE — ALTERNATIVES LIST
// ═══════════════════════════════════════════════════════════
function AlternativesStep({ data, onSelect, loading }) {
    if (!data) return <div className="text-center py-8"><Loader2 className="w-5 h-5 animate-spin mx-auto text-blue-500" /></div>;

    const alternatives = data.alternatives || data.candidates || [];

    return (
        <div className="space-y-4">
            <div className="data-card p-4">
                <h4 className="text-sm font-semibold text-zinc-700 dark:text-zinc-200 mb-2 flex items-center gap-1.5">
                    <ArrowLeftRight className="w-4 h-4 text-violet-500" /> Available Alternatives
                </h4>
                <p className="text-xs text-zinc-400 mb-3">{alternatives.length} option(s) found, sorted by disruption score</p>

                {alternatives.length > 0 ? (
                    <div className="space-y-2">
                        {alternatives.map((alt, i) => (
                            <button key={i} onClick={() => onSelect({ selected_alternative: i })}
                                className="w-full flex items-center justify-between px-4 py-3 rounded-lg border border-zinc-200 dark:border-zinc-700 hover:border-violet-400 hover:bg-violet-50 dark:hover:bg-violet-900/10 transition-all text-left">
                                <div>
                                    <div className="text-xs font-medium text-zinc-700 dark:text-zinc-200">
                                        {alt.label || alt.description || `Option ${i + 1}`}
                                    </div>
                                    <div className="text-[0.6rem] text-zinc-400">
                                        Disruption: {alt.disruption_score ?? 'Low'} · {alt.affected_events ?? 0} affected
                                    </div>
                                </div>
                                <Badge variant="outline" className={`text-[0.55rem] ${(alt.disruption_score ?? 0) <= 2 ? 'text-emerald-500' :
                                    (alt.disruption_score ?? 0) <= 5 ? 'text-amber-500' : 'text-red-500'
                                    }`}>
                                    {(alt.disruption_score ?? 0) <= 2 ? 'Low impact' :
                                        (alt.disruption_score ?? 0) <= 5 ? 'Medium' : 'High'}
                                </Badge>
                            </button>
                        ))}
                    </div>
                ) : (
                    <div className="text-xs text-zinc-400 text-center py-4 border border-dashed border-zinc-200 dark:border-zinc-700 rounded-lg">
                        No alternatives found — try a different change type or scope
                    </div>
                )}
            </div>
        </div>
    );
}

// ═══════════════════════════════════════════════════════════
// PIPELINE STEP CONFIGURATIONS
// ═══════════════════════════════════════════════════════════
const PIPELINE_CONFIGS = {
    blueprint: [
        { id: 'scope', label: 'Scope', icon: Target },
        { id: 'feasibility', label: 'Feasibility', icon: BarChart3 },
        { id: 'template_discovery', label: 'Discovery', icon: Layout },
        { id: 'weekly_pattern', label: 'Pattern', icon: Calendar },
        { id: 'populate', label: 'Timeline', icon: Layers },
        { id: 'save', label: 'Save', icon: Save },
    ],
    fixed_day: [
        { id: 'day_select', label: 'Select Day', icon: Calendar },
        { id: 'diff_check', label: 'Diff Check', icon: BarChart3 },
        { id: 'special_events', label: 'Special Events', icon: Zap },
        { id: 'generate', label: 'Generate', icon: Zap },
        { id: 'sandbox', label: 'Preview', icon: Eye },
        { id: 'publish', label: 'Publish', icon: Upload },
    ],
    post_update: [
        { id: 'trigger', label: 'Trigger', icon: Zap },
        { id: 'impact_analysis', label: 'Impact', icon: AlertTriangle },
        { id: 'alternatives', label: 'Alternatives', icon: ArrowLeftRight },
        { id: 'apply', label: 'Apply', icon: CheckCircle2 },
    ],
};

export default function PipelineWizard({ pipelineType = 'blueprint', existingSessionId = null, onClose }) {
    const [sessionId, setSessionId] = useState(existingSessionId);
    const [currentStep, setCurrentStep] = useState(0);
    const [completedSteps, setCompletedSteps] = useState([]);
    const [stepResults, setStepResults] = useState({});
    const [loading, setLoading] = useState(false);
    const [completed, setCompleted] = useState(false);
    const [templates, setTemplates] = useState([]);
    const [pipelineFaculty, setPipelineFaculty] = useState([]);
    const [pipelineBatches, setPipelineBatches] = useState([]);
    const [pipelineScope, setPipelineScope] = useState({});
    const advancingRef = useRef(false);  // Guard against auto-advance re-entry

    // Start or load the pipeline session
    useEffect(() => {
        (async () => {
            try {
                setLoading(true);
                // Load templates, faculty, batches for all pipeline steps
                const [tRes, fRes, bRes] = await Promise.all([
                    api.get('/api/templates', { params: { session_id: 'none' } }),
                    api.get('/api/faculty'),
                    api.get('/api/batches'),
                ]);
                setTemplates(tRes.data || []);
                setPipelineFaculty(fRes.data || []);
                setPipelineBatches(bRes.data || []);

                if (existingSessionId) {
                    const res = await api.get(`/api/pipeline/sessions/${existingSessionId}`);
                    const state = res.data;
                    if (state && state.step_data) {
                        const mappedResults = {};
                        Object.keys(state.step_data).forEach(k => {
                            // Attempt to map raw dictionary keys back to step names
                            // Simple heuristic: if the step data has a 'feasibility' key, map it to 'feasibility' step name, etc.
                            mappedResults[k] = state.step_data[k];
                        });
                        // Just load all into state to be safe, the component accesses it if it exists.
                        setStepResults(state.step_data);
                    }
                    if (state.status === 'completed') {
                        setCompleted(true);
                    } else if (state.status === 'error') {
                        // Bug 3 fix: detect stalled/error sessions and offer recovery
                        toast.error('Previous pipeline session encountered an error. Starting fresh.');
                        const r = await api.post('/api/pipeline/start', { pipeline_type: pipelineType, scope: {} });
                        setSessionId(r.data.id);
                        setCurrentStep(1);
                    } else {
                        setCurrentStep(state.current_step);
                        setCompletedSteps(Array.from(new Set(Array.from({ length: state.current_step - 1 }, (_, i) => i + 1))));
                    }
                } else {
                    const r = await api.post('/api/pipeline/start', { pipeline_type: pipelineType, scope: {} });
                    setSessionId(r.data.id);
                    setCurrentStep(1);
                }
            } catch (err) {
                toast.error('Failed to initialize pipeline: ' + (err.response?.data?.detail || err.message));
            } finally {
                setLoading(false);
            }
        })();
    }, [pipelineType, existingSessionId]);

    const [progress, setProgress] = useState(0);
    const [progressMsg, setProgressMsg] = useState('');

    const advanceStep = useCallback(async (stepInput = {}) => {
        if (!sessionId || advancingRef.current) return;
        advancingRef.current = true;
        try {
            setLoading(true);
            setProgress(0);
            // Bug 5 fix: descriptive transition message for heavy steps
            const currentStepName = (PIPELINE_CONFIGS[pipelineType] || [])[currentStep - 1]?.label || '';
            setProgressMsg(currentStepName.includes('Generat') ? 'Preparing Solver Environment...' : 'Dispatching...');

            // POST triggers background execution (returns 202 immediately)
            await api.post(`/api/pipeline/${sessionId}/step`, stepInput);

            // Connect to SSE stream for real-time progress
            const backendUrl = process.env.REACT_APP_BACKEND_URL || process.env.REACT_APP_API_URL || 'http://localhost:8000';
            const es = new EventSource(`${backendUrl}/api/pipeline/${sessionId}/stream`);

            await new Promise((resolve, reject) => {
                const timeout = setTimeout(() => {
                    es.close();
                    reject(new Error('Pipeline step timed out (120s)'));
                }, 120000);

                let gotUpdate = false;

                // Safety poll: check every 3s if backend already finished
                const safetyPoll = setInterval(async () => {
                    if (gotUpdate) return; // SSE is working, skip poll
                    try {
                        const pollRes = await api.get(`/api/pipeline/sessions/${sessionId}`);
                        const st = pollRes.data;
                        if (st.current_step != null && st.current_step > currentStep) {
                            // Step already completed — SSE missed it
                            clearTimeout(timeout);
                            clearInterval(safetyPoll);
                            es.close();
                            setProgress(100);
                            setProgressMsg('Step completed');
                            resolve();
                        } else if (st.status === 'error') {
                            clearTimeout(timeout);
                            clearInterval(safetyPoll);
                            es.close();
                            reject(new Error('Pipeline step failed'));
                        }
                    } catch { /* silent */ }
                }, 3000);

                es.onmessage = (event) => {
                    try {
                        const data = JSON.parse(event.data);
                        gotUpdate = true;
                        setProgress(data.progress || 0);
                        setProgressMsg(data.message || '');

                        if (data.status === 'done' || data.status === 'error') {
                            clearTimeout(timeout);
                            clearInterval(safetyPoll);
                            es.close();
                            if (data.status === 'error') {
                                reject(new Error(data.message));
                            } else {
                                resolve();
                            }
                        }
                    } catch { /* ignore parse errors */ }
                };

                es.onerror = () => {
                    // SSE might disconnect — try to poll state instead
                    clearTimeout(timeout);
                    clearInterval(safetyPoll);
                    es.close();
                    resolve(); // Proceed to state poll
                };
            });

            // Fetch the actual pipeline state after completion
            const stateRes = await api.get(`/api/pipeline/sessions/${sessionId}`);
            const state = stateRes.data;

            if (state.step_data) {
                setStepResults(state.step_data);
            }

            if (state.current_step != null) {
                const stepNum = state.current_step;
                setCompletedSteps(prev => {
                    const next = [...prev];
                    for (let i = 1; i < stepNum; i++) if (!next.includes(i)) next.push(i);
                    return next;
                });

                if (state.status === 'completed' || state.completed) {
                    setCompleted(true);
                } else {
                    setCurrentStep(stepNum); // The backend current_step is already incremented to the NEXT step index to work on
                }
            }

            setProgress(100);
            setProgressMsg('');
            return state;
        } catch (err) {
            toast.error('Step error: ' + (err.message || 'Unknown error'));
        } finally {
            setLoading(false);
            setProgress(0);
            advancingRef.current = false;
        }
    }, [sessionId]);

    // Auto-advance for backend analytical steps that don't need user input to start
    useEffect(() => {
        if (!sessionId || loading || completed || advancingRef.current) return;

        // Blueprint: auto-advance feasibility (step 2) and template_discovery (step 3)
        if (pipelineType === 'blueprint') {
            if (currentStep === 2 && !stepResults.feasibility) {
                advanceStep({});
            } else if (currentStep === 3 && !stepResults.template_discovery && !stepResults.discovery) {
                advanceStep({});
            }
        }
        // Fixed-day: auto-advance diff_check (step 2)
        if (pipelineType === 'fixed_day') {
            if (currentStep === 2 && !stepResults.diffs) {
                advanceStep({});
            }
        }
        // Post-update: auto-advance impact_analysis (step 2)
        if (pipelineType === 'post_update') {
            if (currentStep === 2 && !stepResults.impact) {
                advanceStep({});
            }
        }
    }, [currentStep, loading, sessionId, completed, pipelineType]);

    // ── Redirect to CalendarPanel for tentative preview after sandbox step ──
    useEffect(() => {
        if (pipelineType !== 'fixed_day' || loading || advancingRef.current) return;
        // After sandbox step completes, currentStep advances to 5 or 6
        // When we have generated data and sandbox events were created, redirect
        if (currentStep >= 5 && stepResults.generated) {
            const store = useCalendarStore.getState();
            const generated = Array.isArray(stepResults.generated) ? stepResults.generated : [];
            const totalEvents = generated.reduce((sum, d) => sum + (d.events?.length || 0), 0);
            const targetDate = generated[0]?.date || pipelineScope.target_date || null;

            store.setTentativeSession(sessionId, {
                totalEvents,
                coverage: generated[0]?.coverage_pct ?? null,
                dropped: generated.reduce((sum, d) => sum + (d.dropped_count || 0), 0),
                scope: pipelineScope,
                generatedSchedules: generated,
            }, targetDate);

            if (targetDate) {
                store.setCurrentDate(new Date(targetDate + 'T00:00:00+05:30'));
            }
            store.setView('day');
            store.setVisibleCalendars(new Set(['sandbox']));

            // Mark completed so handleClose doesn't nuke the session
            setCompleted(true);

            // Navigate to calendar
            onClose?.();
            window.location.hash = '';
            window.history.pushState({}, '', '/dashboard/calendar');
            window.dispatchEvent(new PopStateEvent('popstate'));
        }
    }, [currentStep, stepResults.generated, pipelineType]);

    const handleClose = useCallback(async () => {
        if (sessionId && !completed) {
            try { await api.delete(`/api/pipeline/${sessionId}`); } catch { /* silent */ }
        }
        onClose?.();
    }, [sessionId, completed, onClose]);

    if (!sessionId && !loading) {
        return <div className="text-center py-12 text-sm text-zinc-400">Failed to initialize pipeline</div>;
    }

    return (
        <div className="flex flex-col h-full">
            {/* Header */}
            <div className="flex items-center justify-between px-4 py-3 border-b border-zinc-200 dark:border-zinc-700">
                <div className="flex items-center gap-2">
                    <Button variant="ghost" size="sm" onClick={handleClose} className="h-7 w-7 p-0">
                        <X className="w-4 h-4" />
                    </Button>
                    <h2 className="text-sm font-semibold text-zinc-800 dark:text-zinc-100">
                        {pipelineType === 'blueprint' ? 'Tentative Blueprint' :
                            pipelineType === 'fixed_day' ? 'Fixed Day Scheduling' :
                                'Post-Schedule Update'}
                    </h2>
                    {sessionId && <Badge variant="outline" className="text-[0.55rem]">{sessionId.substring(0, 8)}</Badge>}
                </div>
                {loading && <Loader2 className="w-4 h-4 animate-spin text-blue-500" />}
            </div>

            {/* Progress Bar */}
            {loading && progress >= 0 && (
                <div className="px-4 py-2 border-b border-zinc-200 dark:border-zinc-700">
                    <Progress
                        value={progress}
                        label={progressMsg || 'Processing...'}
                        color={progress >= 100 ? 'bg-emerald-500' : 'bg-blue-500'}
                    />
                </div>
            )}

            {/* Step Indicator */}
            {!completed && <StepIndicator steps={PIPELINE_CONFIGS[pipelineType] || PIPELINE_CONFIGS.blueprint} currentStep={currentStep} completedSteps={completedSteps} />}

            {/* Step Content */}
            <ScrollArea className="flex-1 p-4">
                {completed ? (
                    <CompletionScreen result={stepResults[Object.keys(stepResults).pop()]} onClose={handleClose}
                        pipelineType={pipelineType} />
                ) : pipelineType === 'blueprint' ? (
                    /* ── Blueprint Steps ── */
                    currentStep === 1 ? (
                        <ScopeStep onSubmit={(data) => { setPipelineScope(data); advanceStep(data); }} loading={loading} />
                    ) : currentStep === 2 ? (
                        <FeasibilityStep data={stepResults.feasibility} onContinue={() => advanceStep({})} loading={loading} />
                    ) : currentStep === 3 ? (
                        <TemplateDiscoveryStep data={stepResults.discovery || stepResults.template_discovery} onContinue={(d) => advanceStep(d)} loading={loading} />
                    ) : currentStep === 4 ? (
                        <WeeklyPatternStep templates={templates} onSubmit={advanceStep} loading={loading} />
                    ) : currentStep === 5 ? (
                        <PopulateStep data={stepResults.populate || stepResults.weekly_pattern} onContinue={() => advanceStep({})} loading={loading} />
                    ) : currentStep === 6 ? (
                        <SaveStep onSubmit={advanceStep} loading={loading}
                            previousData={stepResults.populate || stepResults.weekly_pattern} />
                    ) : <LoadingFallback />
                ) : pipelineType === 'fixed_day' ? (
                    /* ── Fixed Day Steps (backend: day_select → diff_check → special_events → generate → sandbox → publish) ── */
                    currentStep === 1 ? (
                        <FixedDaySelectStep onSubmit={(data) => {
                            setPipelineScope({ dates: data.target_date ? [data.target_date] : [], target_date: data.target_date });
                            advanceStep({
                                dates: data.target_date ? [data.target_date] : [],
                                blueprint_id: data.template_id,
                                ...(data.scope_assignment_ids ? { assignment_ids: data.scope_assignment_ids } : {}),
                                ...(data.reward_weights ? { reward_weights: data.reward_weights } : {}),
                                ...(data.settings_override && Object.keys(data.settings_override).length > 0 ? { settings_override: data.settings_override } : {}),
                            });
                        }} loading={loading} />
                    ) : currentStep === 2 ? (
                        /* diff_check — auto-advanced, shows template match results */
                        <FeasibilityStep data={stepResults.diffs ? { feasible: true, message: `${stepResults.diffs.filter(d => d.can_reuse).length} of ${stepResults.diffs.length} days can reuse templates`, diffs: stepResults.diffs } : null}
                            onContinue={() => advanceStep({})} loading={loading} />
                    ) : currentStep === 3 ? (
                        /* special_events — pass through for now */
                        <div className="space-y-3">
                            <div className="data-card p-4">
                                <h4 className="text-sm font-semibold text-zinc-700 dark:text-zinc-200 mb-2">Special Events (Optional)</h4>
                                <p className="text-xs text-zinc-400 mb-3">Add any tests, labs, or fixed events before generating the schedule.</p>
                                <Button onClick={() => advanceStep({ events: [] })} disabled={loading} className="w-full bg-blue-600 hover:bg-blue-700">
                                    {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Play className="w-4 h-4 mr-2" />}
                                    Skip & Generate Schedule
                                </Button>
                            </div>
                        </div>
                    ) : currentStep === 4 ? (
                        /* generate — shows solver results with calendar day-view */
                        stepResults.generated ? (
                            <div className="space-y-3">
                                <DayScheduleViewer
                                    schedules={stepResults.generated}
                                    faculty={pipelineFaculty}
                                    batches={pipelineBatches}
                                />
                                <Button onClick={() => advanceStep({})} disabled={loading} className="w-full bg-emerald-600 hover:bg-emerald-700">
                                    Preview in Sandbox →
                                </Button>
                            </div>
                        ) : <LoadingFallback />
                    ) : currentStep === 5 ? (
                        /* sandbox — redirecting to calendar view */
                        <div className="flex flex-col items-center justify-center py-16 gap-4">
                            <Loader2 className="w-8 h-8 animate-spin text-emerald-500" />
                            <p className="text-sm text-zinc-500">Preparing calendar preview...</p>
                        </div>
                    ) : currentStep === 6 ? (
                        /* also redirecting (fallback) */
                        <div className="flex flex-col items-center justify-center py-16 gap-4">
                            <Loader2 className="w-8 h-8 animate-spin text-emerald-500" />
                            <p className="text-sm text-zinc-500">Opening calendar...</p>
                        </div>
                    ) : <LoadingFallback />
                ) : pipelineType === 'post_update' ? (
                    /* ── Post-Update Steps (backend: trigger → impact_analysis → alternatives → apply) ── */
                    currentStep === 1 ? (
                        <PostUpdateRequestStep onSubmit={(data) => advanceStep({
                            change_type: data.change_type || 'unavailability',
                            affected_entity: data.affected_entity || {},
                            affected_dates: data.affected_dates || [],
                        })} loading={loading} />
                    ) : currentStep === 2 ? (
                        /* impact_analysis — auto-advanced */
                        stepResults.impact ? (
                            <div className="space-y-3">
                                <div className="data-card p-4">
                                    <h4 className="text-sm font-semibold text-zinc-700 dark:text-zinc-200 mb-2 flex items-center gap-1.5">
                                        <AlertTriangle className="w-4 h-4 text-amber-500" /> Impact Analysis
                                    </h4>
                                    <p className="text-xs text-zinc-400">Severity: {stepResults.impact.max_severity} · {stepResults.impact.total_affected} events affected</p>
                                </div>
                                <Button onClick={() => advanceStep({})} disabled={loading} className="w-full">
                                    Find Alternatives →
                                </Button>
                            </div>
                        ) : <LoadingFallback />
                    ) : currentStep === 3 ? (
                        <AlternativesStep data={stepResults.alternatives}
                            onSelect={(data) => advanceStep({ actions: [data] })} loading={loading} />
                    ) : currentStep === 4 ? (
                        <CompletionScreen result={stepResults.applied || stepResults} onClose={handleClose}
                            pipelineType="post_update" />
                    ) : <LoadingFallback />
                ) : <LoadingFallback />}
            </ScrollArea>
        </div>
    );
}
